import React, { useEffect, useState } from "react";
import SelectOption from "./SelectOption";
import { get_email_list } from "../apis/GetEmailList";
import { add_email } from "../apis/AddEmail";
import { add_email_string } from "../apis/AddEmailString";
import { modify_email } from "../apis/ModifyEmail";
import { get_email_by_id } from "../apis/GetEmailById";
import { delete_email } from "../apis/DeleteEmail";
import Loading from "../pages/Loading";
import ConfirmToast from "../pages/Toast";
import NotificationToast from "../Component/Toast";
import AddEmailModal from "./AddEmailModal";
import PaginationFooter from "./PaginationFooter";
import "../css/user.scss";
import Cookies from "js-cookie";
import { setAPIAuthorizationToken } from "../apis/API";
import { useTranslation } from "../contexts/LocaleContext";

const AddOptionsModal = ({ isOpen, onClose, onAddOne, onAddBulk }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t ? t("emails.add") : "Add contacts"}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div
          className="modal-body add-options-list"
          style={{ backgroundColor: "transparent" }}
        >
          <button
            type="button"
            className="option-card"
            onClick={() => {
              onAddOne();
              onClose();
            }}
          >
            <div className="option-left">
              <div className="option-icon">
                {" "}
                <img alt="" src="/main/one.png" />
              </div>
            </div>
            <div className="option-right">
              <div className="option-title">
                {t ? t("emails.add_one") : "Add one email"}
              </div>
              <div className="option-desc">
                {t
                  ? t("emails.add_one_desc") ||
                    "If you want to add one email, you can use this option"
                  : "If you want to add one email, you can use this option"}
              </div>
            </div>
          </button>

          <button
            type="button"
            className="option-card"
            onClick={() => {
              onAddBulk();
              onClose();
            }}
          >
            <div className="option-left">
              <div className="option-icon">
                {" "}
                <img alt="" src="/main/many.png" />
              </div>
            </div>
            <div className="option-right">
              <div className="option-title">
                {t ? t("emails.add_many") : "Add emails"}
              </div>
              <div className="option-desc">
                {t
                  ? t("emails.add_many_desc") ||
                    "Add multiple emails at once by manually copying and pasting information"
                  : "Add multiple emails at once by manually copying and pasting information"}
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal for bulk adding emails (textarea of CSV lines)
const BulkAddModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [text, setText] = useState("");
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t ? t("emails.bulk.title") : "Batch add emails"}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form
          className="modal-body single-column"
          onSubmit={(e) => {
            e.preventDefault();
            const lines = String(text)
              .split("\n")
              .map((l) => l.trim())
              .filter((l) => l.length > 0);
            onSubmit(lines);
          }}
        >
          <label style={{ marginBottom: 8 }}>
            {t
              ? t(
                  "emails.bulk.format_hint",
                  "(account,password,key,port,smtp,address | host,port,account,password,SOCKS5)"
                )
              : "(account,password,key,port,smtp,address | host,port,account,password,SOCKS5)"}
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
          />
          <div className="modal-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t ? t("user.modal.cancel") : "Cancel"}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />
                  {t ? t("emails.adding") || "Adding..." : "Adding..."}
                </>
              ) : t ? (
                t("emails.add")
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Emails = () => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [confirmDeleteEmail, setConfirmDeleteEmail] = useState(null);
  const [toastInfo, setToastInfo] = useState({ message: "", type: "" });
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [goToInput, setGoToInput] = useState("1");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalItems, setTotalItems] = useState(0);

  //获取邮箱列表
  // perPage defaults to current pageSize state but can be overridden to ensure callers pass the intended size
  const fetchEmails = async (
    page = 1,
    suppressLoading = false,
    name = "",
    perPage = pageSize,
    status = statusFilter
  ) => {
    try {
      if (!suppressLoading) setIsLoading(true);
      const res = await get_email_list(page, perPage, name, status);
      let items = [];
      let total = 0;
      if (res && res.data) {
        items = res.data.data || [];
        total = res.data.total || items.length;
      }
      setEmails(items);
      setTotalItems(Number(total) || items.length);
    } catch (err) {
      console.error("Failed to fetch emails:", err);
    } finally {
      if (!suppressLoading) setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
    // initial fetch (do not suppress loading)
    fetchEmails(1, false, "", pageSize, statusFilter);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setGoToInput(String(currentPage));
  }, [currentPage]);

  const goToPage = async (page) => {
    const p = Math.max(1, Math.min(totalPages, Number(page) || 1));
    setCurrentPage(p);
    await fetchEmails(p, false, searchName, pageSize, statusFilter);
  };

  const showNotification = (message, type = "success") =>
    setToastInfo({ message, type });

  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const openEditModal = async (email) => {
    // email may be a full object or an id; prefer id if available
    const id = email?.id ?? email?._id ?? email;
    if (!id) {
      setEditingEmail(email || null);
      setShowEmailModal(true);
      return;
    }
    try {
      setIsLoading(true);
      const res = await get_email_by_id(id);
      const data = res && res.data ? res.data : null;
      // API shape may put item in data.data or data
      const item = data && data.data ? data.data : data;
      setEditingEmail(item || null);
      setShowEmailModal(true);
    } catch (err) {
      console.error("Failed to fetch email by id:", err);
      showNotification("Failed to load email details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEditingEmail(null);
  };

  // handle submit from modal (parent performs API calls)
  const handleEmailSubmit = async (form) => {
    setIsSaving(true);
    try {
      if (form.id) {
        // include updateTime when modifying existing email
        const updateTime = new Date().toISOString();
        await modify_email(
          form.id,
          form.mailUser,
          form.mailPassword,
          form.mailKey,
          form.smtpServeraddress,
          form.port,
          form.status,
          form.ip,
          updateTime
        );
        showNotification("Email updated", "success");
      } else {
        await add_email(
          form.mailUser,
          form.mailPassword,
          form.mailKey,
          form.smtpServeraddress,
          form.port,
          form.status,
          form.ip
        );
        showNotification("Email added", "success");
      }
      await fetchEmails(currentPage, true, searchName, pageSize);
      closeEmailModal();
    } catch (err) {
      console.error("Failed to save email:", err);
      showNotification("Failed to save email", "error");
    } finally {
      setIsSaving(false);
    }
    await fetchEmails(currentPage, true, searchName, pageSize, statusFilter);
  };

  const formatDate = (iso) => {
    if (!iso) return "2026-01-04 11:24";
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")} ${String(
        d.getHours()
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch {
      return iso;
    }
  };

  return (
    <div className="users-page ">
      <div className="users-controls-card">
        <div className="controls-left">
          <div className="search-input">
            <input
              placeholder={t ? t("emails.search_placeholder") : "Search here"}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  // show loading overlay while backend responds
                  fetchEmails(1, false, searchName, pageSize, statusFilter);
                }
              }}
            />
            <button
              type="button"
              className="search-icon"
              aria-label="Search"
              onClick={() => {
                setCurrentPage(1);
                // show loading overlay while backend responds
                fetchEmails(1, false, searchName, pageSize, statusFilter);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="m16.875 15.455 4 4.005a.5.5 0 0 1-.02.691l-.7.7a.5.5 0 0 1-.71 0l-4-4.004a2.6 2.6 0 0 1-.3-.36l-.75-1.001a7 7 0 0 1-4.37 1.531A7.011 7.011 0 0 1 6.973 3.69a6.995 6.995 0 0 1 8.518 1.971 7.015 7.015 0 0 1-.046 8.753l1 .691q.234.151.43.35M5.025 10.01a5.003 5.003 0 0 0 5 5.006 5 5 0 0 0 3.535-1.466 5 5 0 0 0 1.465-3.54 5.003 5.003 0 0 0-5-5.006c-2.761 0-5 2.241-5 5.006"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="status-select-wrap">
            <SelectOption
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                fetchEmails(1, false, searchName, pageSize, v);
              }}
              options={[
                { value: "", label: t ? t("filter.all") : "All" },
                { value: "1", label: t ? t("status.on") : "ON" },
                { value: "0", label: t ? t("status.off") : "OFF" },
              ]}
              ariaLabel="状态筛选"
            />
          </div>
        </div>
        <div className="controls-right">
          <button className="btn-add" onClick={() => setShowAddOptions(true)}>
            {t ? t("emails.add") : "Add emails"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : emails.length === 0 ? (
        <div>{t ? t("emails.no_items") : "No emails"}</div>
      ) : (
        <div className="users-table-card">
          <div className="table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t ? t("emails.table.mail_user") : "Mail User"}</th>
                  <th>{t ? t("emails.table.ip") : "IP"}</th>
                  <th>{t ? t("emails.table.mail_key") : "Mail Key"}</th>
                  <th>
                    {t ? t("emails.table.mail_password") : "Mail Password"}
                  </th>
                  <th>{t ? t("emails.table.port") : "Port"}</th>
                  <th>{t ? t("emails.table.smtp_server") : "SMTP Server"}</th>
                  <th>{t ? t("emails.table.status") : "Status"}</th>
                  <th>{t ? t("emails.table.date") : "Date"}</th>
                  <th>{t ? t("emails.table.operation") : "Operation"}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <Loading />
                ) : emails.length === 0 ? (
                  <div>No emails</div>
                ) : (
                  emails.map((mail, idx) => {
                    const id = mail.id ?? mail._id ?? idx;
                    return (
                      <tr key={id}>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-label">
                              {t ? t("emails.table.mail_user") : "Mail User"}
                            </span>
                            <span className="cell-value">
                              {mail.mailUser ?? "-"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-label">
                              {t ? t("emails.table.ip") : "IP"}
                            </span>
                            <span className="cell-value">{mail.ip ?? "-"}</span>
                          </div>
                        </td>
                        <td>
                          <div
                            className="cell-with-label"
                            style={{ maxWidth: 180 }}
                          >
                            <span className="cell-label">
                              {t ? t("emails.table.mail_key") : "Mail Key"}
                            </span>
                            <span
                              className="cell-value"
                              style={{
                                display: "block",
                                maxWidth: 180,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {mail.mailKey ?? "-"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div
                            className="cell-with-label"
                            style={{ maxWidth: 160 }}
                          >
                            <span className="cell-label">
                              {t
                                ? t("emails.table.mail_password")
                                : "Mail Password"}
                            </span>
                            <span
                              className="cell-value"
                              style={{
                                display: "block",
                                maxWidth: 160,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {mail.mailPassword ?? "-"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-label">
                              {t ? t("emails.table.port") : "Port"}
                            </span>
                            <span className="cell-value">
                              {mail.port ?? "-"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-label">
                              {t
                                ? t("emails.table.smtp_server")
                                : "SMTP Server"}
                            </span>
                            <span className="cell-value">
                              {mail.smtpServeraddress ??
                                mail.smtpServerAddress ??
                                "-"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-label">
                              {t ? t("emails.table.status") : "Status"}
                            </span>
                            <span className="cell-value">
                              {mail.status ? (
                                <span
                                  className={
                                    mail.status === "1"
                                      ? "status-pill status-available"
                                      : "status-pill status-off"
                                  }
                                >
                                  {t
                                    ? mail.status === "1"
                                      ? t("status.on")
                                      : t("status.off")
                                    : mail.status === "1"
                                    ? "ON"
                                    : "OFF"}
                                </span>
                              ) : (
                                "-"
                              )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-label">
                              {t ? t("emails.table.date") : "Date"}
                            </span>
                            <span className="cell-value">
                              {formatDate(mail.updateTime)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-label">
                            <span className="cell-value">
                              <div className="op-buttons">
                                <button
                                  className="btn-edit"
                                  onClick={() => {
                                    openEditModal(mail);
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      fill="#000"
                                      fillRule="evenodd"
                                      d="m19.902 3.44.659.658c.28.28.439.66.439 1.058v.758c.005.404-.153.793-.44 1.078l-1.845 1.816a.5.5 0 0 1-.708 0l-2.804-2.844a.5.5 0 0 1 0-.699L17.01 3.44A1.5 1.5 0 0 1 18.087 3h.758c.397 0 .777.158 1.057.44m-3.082 7.245a.5.5 0 0 0 0-.7l-2.854-2.813a.5.5 0 0 0-.708 0l-8.052 8.053a2 2 0 0 0-.449.679l-1.716 4.331a.5.5 0 0 0 .19.619.5.5 0 0 0 .538.11l4.33-1.757c.255-.101.486-.254.679-.449z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                                <button
                                  className="btn-delete"
                                  onClick={() => {
                                    setConfirmDeleteEmail(id);
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      fill="#000"
                                      fillRule="evenodd"
                                      d="M20 5.5v-1a.5.5 0 0 0-.5-.5H15V3a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1H4.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h15a.5.5 0 0 0 .5-.5M7.72 18h8.56L17 8h2l-.85 12.14a2 2 0 0 1-2 1.86H7.87a2 2 0 0 1-2-1.86L5 8h2z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="users-table-footer" style={{ marginTop: 12 }}>
            <PaginationFooter
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setCurrentPage(1);
                fetchEmails(1, false, searchName, n, statusFilter);
              }}
              currentPage={currentPage}
              onPageChange={(p) => goToPage(p)}
              totalPages={totalPages}
              goToValue={goToInput}
              onGoToValueChange={(v) => setGoToInput(v)}
              onGoClick={() => goToPage(goToInput)}
            />
          </div>
        </div>
      )}
      {/* Email add/edit modal */}
      <AddEmailModal
        isOpen={showEmailModal}
        onClose={closeEmailModal}
        initialData={editingEmail || undefined}
        onSubmit={handleEmailSubmit}
        isLoading={isSaving}
      />
      <AddOptionsModal
        isOpen={showAddOptions}
        onClose={() => setShowAddOptions(false)}
        onAddOne={() => setShowEmailModal(true)}
        onAddBulk={() => setShowBulkModal(true)}
        showNotification={showNotification}
      />
      <BulkAddModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        isSubmitting={isBulkSubmitting}
        onSubmit={async (lines) => {
          try {
            setIsBulkSubmitting(true);
            await add_email_string(lines);
            showNotification("Bulk emails added", "success");
            await fetchEmails(
              currentPage,
              false,
              searchName,
              pageSize,
              statusFilter
            );
            setShowBulkModal(false);
          } catch (err) {
            console.error("Bulk add failed:", err);
            showNotification("Failed to add bulk emails", "error");
          } finally {
            setIsBulkSubmitting(false);
          }
        }}
      />
      {/* confirm delete toast */}
      <ConfirmToast
        show={!!confirmDeleteEmail}
        onClose={() => setConfirmDeleteEmail(null)}
        confirmProps={{
          title: t ? t("confirm.delete_email.title") : "Delete Email",
          description: confirmDeleteEmail
            ? t
              ? t("confirm.delete_email.description")
              : `Are you sure you want to delete this email account?`
            : "",
          confirmLabel: t ? t("confirm.delete") : "Delete",
          cancelLabel: t ? t("confirm.cancel") : "Cancel",
          onConfirm: async () => {
            try {
              setIsSaving(true);
              if (confirmDeleteEmail) {
                await delete_email(confirmDeleteEmail);
                showNotification(
                  t ? t("notification.email_deleted") : "Email deleted",
                  "success"
                );
                await fetchEmails(
                  currentPage,
                  false,
                  searchName,
                  pageSize,
                  statusFilter
                );
              }
            } catch (err) {
              console.error("Failed to delete email:", err);
              showNotification(
                t
                  ? t("notification.failed_delete_email")
                  : "Failed to delete email",
                "error"
              );
            } finally {
              setIsSaving(false);
              setConfirmDeleteEmail(null);
            }
          },
          onCancel: () => setConfirmDeleteEmail(null),
        }}
      />
      {/* top notification */}
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
    </div>
  );
};

export default Emails;
